import { router, useFocusEffect } from "expo-router";
import { camelize } from "fast-case";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ScrollView,
  View,
  TouchableWithoutFeedback,
  Text,
  ActivityIndicator,
} from "react-native";
import { useDispatch } from "react-redux";

import Logo from "@/assets/logo.svg";
import PenIcon from "@/assets/penicon.svg";
import Copyright from "@/components/Copyright";
import Loader from "@/components/Loader";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import ButtonLogout from "@/components/button/ButtonLogout";
import Input from "@/components/input/Input";
import InputChoose from "@/components/input/InputChoose";
import InputDate from "@/components/input/InputDate";
import InputDropList from "@/components/input/InputDropList";
import InputSelection from "@/components/input/InputSelection";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { sendRequest } from "@/utils/ApiUtil";
import {
  EnumsOnboarding,
  fieldsOnboarding,
  addressFields,
  kycFields,
  investmentProfileFields,
} from "@/utils/EnumsOnboarding";
import {
  extractErrors,
  submitUpdateIndividuals,
  fieldsToUseCodeInHookForm,
} from "@/utils/onboarding/OnboardingUtils";
//Type to use in React Hook Form.
import { FormData } from "@/utils/onboarding/Types";

interface ApplicationError {
  description: string;
  field_name: string;
  status: string;
}

interface RequestData {
  code: string;
  description: string;
  country: string;
  state: string;
  stateCode: string;
  city: string;
}

export default function Review() {
  const [errors, setErrors] = useState<
    { fieldName: string; errorDescription: string }[]
  >([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [editStatus, setEditStatus] = useState<{ [key: string]: boolean }>({});
  const [isFieldEditing, setIsFieldEditing] = useState<{
    [key: string]: boolean;
  }>({});
  const [inputData, setInputData] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [inputArray, setInputArray] = useState<{
    [key: string]: { value: string; code: string }[];
  }>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentDropListInput, setCurrentDropListInput] = useState("");
  const [individualsWithError, setIndividualsWithError] = useState([]);
  const [currentApplication, setCurrentApplication] = useState<any>({});
  const [individualsFieldsModified, setIndividualsFieldsModified] =
    useState<any>({});
  const initialZIndex = 200;
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const globalValues = useAppSelector((state: any) => state);

  const camelizeFieldName = (fieldName: string) => {
    if (fieldName.includes(".")) {
      const parts = fieldName.split(".");
      const lastPart = parts[parts.length - 1];
      return camelize(lastPart);
    } else {
      return camelize(fieldName);
    }
  };

  const allFieldsFinished = errors.every(
    (errorKey) => editStatus[errorKey.fieldName]
  );

  const allIndividualFieldsFinished =
    individualsWithError.length > 0
      ? individualsWithError?.every((individual) =>
          individual.validation_errors?.every(
            (error) =>
              editStatus[
                `${camelizeFieldName(error.field_name)}-${
                  individual.individual_id
                }`
              ]
          )
        )
      : true;

  const { control, setValue, handleSubmit, formState } = useForm<FormData>({
    mode: "onSubmit",
  });

  const { isSubmitting } = formState;

  useFocusEffect(
    React.useCallback(() => {
      getStatus();
      return () => {};
    }, [])
  );

  const fetchApplication = async () => {
    try {
      const request = await sendRequest("get", "/applications", null, true);
      return request;
    } catch (error: any) {
      console.log(error);
    }
  };

  const getStatus = async () => {
    try {
      const getApplication = await fetchApplication();
      const findApplicationChangesRequested = getApplication.application.find(
        (app) =>
          app.status === "CHANGES_REQUESTED" || app.status === "INCOMPLETE"
      );

      setCurrentApplication(findApplicationChangesRequested);
      const fields =
        findApplicationChangesRequested.customerDetailsIndividual ||
        findApplicationChangesRequested.customerDetailsCorporation;

      dispatch(setForm({ applicationFields: fields }));
      try {
        const response = await sendRequest(
          "get",
          "/application-status",
          { applicationId: findApplicationChangesRequested.id },
          true
        );

        const fieldErrors =
          (response.validation_errors ||
            response.application_individual_errors[0]?.validation_errors) ??
          [];

        const checkIndividualsWithError =
          response.application_individual_errors?.filter(
            (individual) => individual.validation_errors.length > 0
          );

        if (checkIndividualsWithError) {
          setIndividualsWithError(checkIndividualsWithError);
        }

        // Changes field_name with errors find in response to fields name pattern. Example = telephone_number -> telephoneNumber.
        const extractedErrors = fieldErrors
          ? fieldErrors
              .map((error: ApplicationError) => {
                const fieldName = camelizeFieldName(error.field_name);
                const errorDescription = error.description;

                if (fieldsOnboarding.includes(fieldName)) {
                  return { fieldName, errorDescription };
                }
                return null;
              })
              .filter(Boolean)
          : [];

        if (
          (extractedErrors.length === 0 || !extractedErrors) &&
          (checkIndividualsWithError.length === 0 || !checkIndividualsWithError)
        ) {
          router.replace("/documents");
        } else {
          //For each error, set the initial value based on user application and the fieldEditedStatus to false.
          extractedErrors.forEach((error) => {
            const { fieldName } = error;
            setValue(fieldName as keyof FormData, fields[fieldName] || "");
          });
          setErrors(extractedErrors);
          setEditStatus({});
          setIsDataLoaded(true);
        }
      } catch (error: any) {
        console.log(error);
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const applicationType = currentApplication.applicationType;
      let url;
      let dataRequest;
      let dataToMap: { value: keyof RequestData; code: keyof RequestData };
      const dataRequestCountry = { searchTerm };
      const dataRequestState = {
        countryCode:
          applicationType === "INDIVIDUAL"
            ? globalValues.applicationFields.mailingAddress.countryCode
            : globalValues.applicationFields.registeredAddress.countryCode,
        searchTerm,
      };
      const dataRequestStateIncorporation = {
        countryCode: globalValues.applicationFields.countryOfIncorporation,
        searchTerm,
      };
      const dataRequestCity = {
        countryCode:
          applicationType === "INDIVIDUAL"
            ? globalValues.applicationFields.mailingAddress.countryCode
            : globalValues.applicationFields.registeredAddress.countryCode,
        stateCode:
          applicationType === "INDIVIDUAL"
            ? globalValues.applicationFields.mailingAddress.state
            : globalValues.applicationFields.registeredAddress.state,
        searchTerm,
      };
      const dataRequestNaics = {
        searchTerm,
      };
      switch (currentDropListInput) {
        case "countryCode":
        case "fundsSendReceiveJurisdictions":
        case "businessJurisdictions":
        case "countryOfIncorporation":
        case "country":
        case "tradeJurisdictions":
        case "citizenship":
        case "nationality":
          url = "/countries";
          dataRequest = dataRequestCountry;
          dataToMap = { value: "country", code: "code" };
          break;
        case "state":
          url = "/states";
          dataRequest = dataRequestState;
          dataToMap = { value: "state", code: "stateCode" };
          break;
        case "city":
          url = "/cities";
          dataRequest = dataRequestCity;
          dataToMap = { value: "city", code: "stateCode" };
          break;
        case "naics":
          url = "/naics-code";
          dataRequest = dataRequestNaics;
          dataToMap = { value: "description", code: "code" };
          break;
        case "naicsDescription":
          url = "/naics-code";
          dataRequest = dataRequestNaics;
          dataToMap = { value: "description", code: "code" };
          break;
        case "stateOfIncorporation":
          url = "/states";
          dataRequest = dataRequestStateIncorporation;
          dataToMap = { value: "state", code: "stateCode" };
          break;
        default:
          break;
      }
      try {
        const response = await sendRequest("get", url, dataRequest);
        const mappedData = response.map((data: RequestData) => ({
          value: data[dataToMap.value],
          code: data[dataToMap.code],
          uniqueKey: `value-${data[dataToMap.code]}`,
        }));
        setInputData(mappedData);
      } catch (error: any) {
        console.log(error);
      }
    };
    if (searchTerm) {
      fetchData();
    }
  }, [searchTerm, currentDropListInput]);

  useEffect(() => {
    const businessJurisdictionsCodes = inputArray.businessJurisdictions?.map(
      (item) => item.code
    );
    if (businessJurisdictionsCodes && businessJurisdictionsCodes.length > 0) {
      setValue("businessJurisdictions", businessJurisdictionsCodes);
    }
    const fundsSendReceiveJurisdictionsCodes =
      inputArray.fundsSendReceiveJurisdictions?.map((item) => item.code);

    if (
      fundsSendReceiveJurisdictionsCodes &&
      fundsSendReceiveJurisdictionsCodes.length > 0
    ) {
      setValue(
        "fundsSendReceiveJurisdictions",
        fundsSendReceiveJurisdictionsCodes
      );
    }
    const engageInActivitiesCodes = inputArray.engageInActivities?.map(
      (item) => item.code
    );
    if (engageInActivitiesCodes && engageInActivitiesCodes.length > 0) {
      setValue("engageInActivities", engageInActivitiesCodes);
    }
    const citizenshipCodes = inputArray.citizenship?.map((item) => item.code);
    if (citizenshipCodes && citizenshipCodes.length > 0) {
      setValue("citizenship", citizenshipCodes);
    }
  }, [inputArray]);

  const handleSelected = (
    errorKey: string,
    data: { value: string; code: string },
    id?: string
  ) => {
    const checkKey = id ? `${errorKey}-${id}` : errorKey;
    if (id) {
      setIndividualsFieldsModified((prevState) => ({
        ...prevState,
        [id]: {
          ...prevState[id],
          [errorKey]: fieldsToUseCodeInHookForm.includes(errorKey)
            ? data.code
            : data.value,
        },
      }));
    } else {
      if (fieldsToUseCodeInHookForm.includes(errorKey)) {
        setValue(errorKey as keyof FormData, data.code);
      } else {
        setValue(errorKey as keyof FormData, data.value);
      }
    }
    setEditStatus((prevState) => ({
      ...prevState,
      [checkKey]: true,
    }));
    setInputData([]);
    setSearchTerm("");
  };

  const handleTextInput = (errorKey: string, text: string, id?: string) => {
    const checkKey = id ? `${errorKey}-${id}` : errorKey;
    if (id) {
      setIndividualsFieldsModified((prevState) => ({
        ...prevState,
        [id]: {
          ...prevState[id],
          [errorKey]: text,
        },
      }));
    } else {
      setValue(errorKey as keyof FormData, text);
    }
    setEditStatus((prevState) => ({
      ...prevState,
      [checkKey]: true,
    }));
  };

  const handleSelection = (
    data: { value: string; code: string },
    name: string,
    id?: string
  ) => {
    const checkName = id ? `${name}-${id}` : name;
    setInputArray((prevState) => {
      const isValueAlreadyPresent = prevState[checkName]?.some(
        (item) => item.value === data.value
      );

      if (isValueAlreadyPresent) {
        return prevState;
      }

      return {
        ...prevState,
        [checkName]: [
          ...(prevState[checkName] || []),
          { value: data.value, code: data.code },
        ],
      };
    });
    if (id) {
      setIndividualsFieldsModified((prevState) => ({
        ...prevState,
        [id]: {
          ...prevState[id],
          [name]: [...(prevState[id]?.[name] || []), data.code],
        },
      }));
    }
    setEditStatus((prevState) => ({
      ...prevState,
      [checkName]: true,
    }));
    setInputData([]);
    setSearchTerm("");
  };

  const handleDeleteItem = (
    key: string,
    value: string,
    code: string,
    individualId?: string
  ) => {
    setInputArray((prevState) => ({
      ...prevState,
      [individualId ? `${key}-${individualId}` : key]: prevState[key]?.filter(
        (item) => item.value !== value
      ),
    }));
  };

  const renderInputFields = (errors: any[], individualId?: string) => {
    return errors.map((error, index) => {
      const { fieldName, errorDescription } = error;
      const zIndex = initialZIndex - index * 10;
      let InputComponent;
      let IsEngage;
      switch (fieldName) {
        case "telephoneNumber":
        case "firstName":
        case "middleName":
        case "lastName":
        case "emailAddress":
        case "passportNumber":
        case "taxReferenceNumber":
        case "unitNumber":
        case "descriptionOfBusinessNature":
        case "addressLine1":
        case "postalCode":
        case "registeredName":
        case "tradingName":
        case "registeredNumber":
        case "websiteAddress":
        case "employerName":
        case "occupation":
        case "employmentDescription":
        case "primarySourceOfFundsDescription":
        case "corporateEntityTypeDescription":
        case "regulationAgency":
        case "regulationAgencyRegulationNumber":
        case "initialDepositSourceCryptoDetails":
        case "initialDepositSourceFiatDetails":
        case "investmentProceedsUse":
        case "knownUnhostedWalletAddresses":
          InputComponent = Input;
          IsEngage = false;
          break;
        case "city":
        case "state":
        case "countryCode":
        case "nationality":
        case "naics":
        case "naicsDescription":
        case "stateOfIncorporation":
        case "countryOfIncorporation":
          InputComponent = InputDropList;
          IsEngage = false;
          break;
        case "dateOfBirth":
        case "establishedOn":
          InputComponent = InputDate;
          IsEngage = false;
          break;
        case "fundsSendReceiveJurisdictions":
        case "businessJurisdictions":
        case "tradeJurisdictions":
        case "citizenship":
          InputComponent = InputSelection;
          IsEngage = false;
          break;
        default:
          InputComponent = InputChoose;
          IsEngage = fieldName === "engageInActivities";
          break;
      }
      return (
        <View
          key={`${fieldName}_${index}`}
          className={`flex-row items-center max-w-80 ${
            zIndex <= 50 ? `z-${zIndex}` : `z-[${zIndex}]`
          }`}
        >
          {InputComponent !== InputDropList &&
          InputComponent !== InputChoose &&
          InputComponent !== InputSelection ? (
            <TouchableWithoutFeedback
              key={fieldName}
              onFocus={() => {
                setIsFieldEditing((prevState) => ({
                  ...prevState,
                  [fieldName]: true,
                }));
                setCurrentDropListInput(fieldName);
              }}
              onBlur={() => {
                setIsFieldEditing((prevState) => ({
                  ...prevState,
                  [fieldName]: false,
                }));
              }}
            >
              <View className="flex-col items-center">
                <View className="flex-row items-center">
                  <InputComponent
                    key={fieldName}
                    name={individualId ? `${"REMOVE"}${fieldName}` : fieldName}
                    label={t(`onboarding/review:field.${fieldName}`)}
                    control={control}
                    handleChange={(text: string) => {
                      handleTextInput(fieldName, text, individualId);
                    }}
                  />
                  {!isFieldEditing[fieldName] &&
                    InputComponent !== InputDate && (
                      <View className="z-50 absolute mr-4 end-0 top-14 items-center justify-center">
                        <PenIcon />
                      </View>
                    )}
                </View>
                {!editStatus[fieldName] && (
                  <View className="max-w-80 mt-[-16px] mb-3">
                    <Text
                      className="text-red text-xs"
                      numberOfLines={undefined}
                    >
                      {errorDescription}
                    </Text>
                  </View>
                )}
              </View>
            </TouchableWithoutFeedback>
          ) : (
            <View className="flex-col items-center">
              <View
                onPointerEnter={() => {
                  setCurrentDropListInput(fieldName);
                  setIsFieldEditing((prevState) => ({
                    ...prevState,
                    [fieldName]: true,
                  }));
                }}
                onPointerLeave={() => {
                  setIsFieldEditing((prevState) => ({
                    ...prevState,
                    [fieldName]: false,
                  }));
                }}
                onTouchStart={() => {
                  setCurrentDropListInput(fieldName);
                  setIsFieldEditing((prevState) => ({
                    ...prevState,
                    [fieldName]: true,
                  }));
                }}
                className="flex-row items-center max-w-80"
              >
                <InputComponent
                  key={fieldName}
                  control={control}
                  name={individualId ? `${"REMOVE"}${fieldName}` : fieldName}
                  label={t(`onboarding/review:field.${fieldName}`)}
                  {...(InputComponent === InputDropList
                    ? {
                        handleChange: (data: {
                          value: string;
                          code: string;
                        }) => {
                          handleSelected(fieldName, data, individualId);
                        },
                        clearValueFunction: () => {
                          setInputData([]);
                        },
                        onSearchChange: (query: string) => {
                          setSearchTerm(query);
                        },
                        placeholder: t("common:placeholder.typetosearch"),
                        data: inputData || [],
                      }
                    : InputComponent === InputChoose && !IsEngage
                      ? {
                          handleChange: (text: string) => {
                            handleTextInput(fieldName, text, individualId);
                          },
                          data: {
                            [EnumsOnboarding[fieldName][0]]:
                              EnumsOnboarding[fieldName][1],
                          },
                        }
                      : InputComponent === InputSelection
                        ? {
                            placeholder: t("common:placeholder.typetosearch"),
                            data: inputData || [],
                            onSearchChange: (query: string) => {
                              setSearchTerm(query);
                            },
                            clearValueFunction: () => {
                              setInputData([]);
                            },
                            handleChange: (data: {
                              value: string;
                              code: string;
                            }) => {
                              handleSelection(data, fieldName, individualId);
                            },
                            selectionArray:
                              inputArray[
                                individualId
                                  ? `${fieldName}-${individualId}`
                                  : fieldName
                              ] || [],
                            deleteItem: (value, code) =>
                              handleDeleteItem(
                                fieldName,
                                value,
                                code,
                                individualId
                              ),
                          }
                        : {
                            isMultipleSelection: true,
                            selectionArray: inputArray.engageInActivities || [],
                            data: {
                              [EnumsOnboarding[fieldName][0]]:
                                EnumsOnboarding[fieldName][1],
                            },
                            handleSelectedItem: (data: {
                              value: string;
                              code: string;
                            }) => {
                              handleSelection(data, fieldName, individualId);
                            },
                            deleteItem: (value, code) =>
                              handleDeleteItem(
                                fieldName,
                                value,
                                code,
                                individualId
                              ),
                          })}
                />
                {!isFieldEditing[fieldName] &&
                  !editStatus[fieldName] &&
                  InputComponent !== InputChoose && (
                    <View className="z-50 absolute mr-4 end-0 top-14 items-center justify-center">
                      <PenIcon />
                    </View>
                  )}
              </View>
              {!editStatus[fieldName] && (
                <View className="max-w-80 mt-[-16px] mb-3">
                  <Text className="text-red text-xs" numberOfLines={undefined}>
                    {errorDescription}
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>
      );
    });
  };

  const renderIndividualsFields = (individuals: any[]) => {
    return individuals.map((individual, index) => {
      const findIndividualData =
        currentApplication.customerDetailsCorporation?.individuals?.filter(
          (ind) =>
            ind.individualId === individual.individual_id ||
            ind.individualId === individual.individualId
        );

      const errors = individual.validation_errors;
      const errorsExtracted = extractErrors(errors);
      const zIndex = initialZIndex - index * 10;
      return (
        <View
          key={individual.individual_id}
          className={`p-2 flex-col items-center border-t border-b border-midgray border-dashed w-full z-[${zIndex}]`}
        >
          <View className="flex-row items-center">
            <Text className="text-lg">{`${findIndividualData[0].firstName ?? ""} ${findIndividualData[0].lastName ?? ""}`}</Text>
          </View>
          <View className="flex-col items-center">
            {renderInputFields(errorsExtracted, individual.individual_id)}
          </View>
        </View>
      );
    });
  };

  const onSubmit = async (data: FormData) => {
    const applicationType = currentApplication.applicationType;
    const applicationId = currentApplication.id;
    const customerDetails: {
      [key: string]:
        | string
        | string[]
        | boolean
        | { [key: string]: string | string[] | boolean };
    } = {};
    const mailingAddress: { [key: string]: string | string[] | boolean } = {};
    const registeredAddress: { [key: string]: string | string[] | boolean } =
      {};
    const kycProfile: { [key: string]: string | string[] | boolean } = {};
    const investmentProfile: { [key: string]: string | string[] | boolean } =
      {};

    for (const [key, value] of Object.entries(data)) {
      if (key.startsWith("REMOVE")) {
        continue;
      }
      if (Array.isArray(value) && kycFields.includes(key)) {
        if (
          !(
            value.length === 1 &&
            value[0] === "" &&
            value[0] === undefined &&
            value[0] === null
          )
        ) {
          kycProfile[key] = value.filter((item) => item !== "OUT");
        }
      } else if (value !== null && value !== undefined && value !== "") {
        if (addressFields.includes(key)) {
          if (applicationType === "INDIVIDUAL") {
            mailingAddress[key] = value;
          } else if (applicationType === "CORPORATION") {
            registeredAddress[key] = value;
          }
        } else if (investmentProfileFields.includes(key)) {
          if (value === "YES") {
            investmentProfile[key] = true;
          } else if (value === "NO") {
            investmentProfile[key] = false;
          } else {
            investmentProfile[key] = value;
          }
        } else if (kycFields.includes(key)) {
          if (value === "YES") {
            kycProfile[key] = true;
          } else if (value === "NO") {
            kycProfile[key] = false;
          } else {
            kycProfile[key] = value;
          }
        } else {
          customerDetails[key] = value;
        }
      }
    }

    if (Object.keys(mailingAddress).length > 0) {
      customerDetails.mailingAddress = mailingAddress;
    }

    if (Object.keys(registeredAddress).length > 0) {
      customerDetails.registeredAddress = registeredAddress;
    }

    if (Object.keys(kycProfile).length > 0) {
      customerDetails.kycProfile = kycProfile;
    }

    if (Object.keys(investmentProfile).length > 0) {
      customerDetails.investmentProfile = investmentProfile;
    }

    try {
      if (individualsWithError.length > 0) {
        await submitUpdateIndividuals(
          individualsWithError,
          individualsFieldsModified,
          applicationId
        );
      }
      if (customerDetails && Object.keys(customerDetails).length > 0) {
        await sendRequest(
          "patch",
          "/applications",
          {
            applicationId,
            update: {
              customerDetails,
            },
          },
          true
        );
      }
      await sendRequest("get", "/application-status", { applicationId }, true);
    } catch (error: any) {
      console.log(error);
    }
  };

  return (
    <ScrollView contentContainerClassName="flex-grow bg-white1">
      <View className="z-50 absolute top-0 right-0 m-8">
        <ButtonLogout />
      </View>
      {!isDataLoaded && (
        <ActivityIndicator
          size="large"
          color="#5D0CBA"
          className="z-50 bg-white1 absolute items-center justify-center w-full h-full"
        />
      )}
      <View className="items-center w-full h-full">
        <Logo className="m-4" />
        <View className="m-2">
          <Title text={t("onboarding/review:title")} />
        </View>
        <Text className="text-darkergray text-center text-lg">
          {t("onboarding/review:subtitle")}
        </Text>
        <View className="z-50 flex-col items-center w-3/4 p-6 lg:border lg:border-solid lg:border-gray lg:rounded-md lg:shadow">
          <View className="z-50 items-center max-w-80">
            {renderInputFields(errors)}
          </View>
          {currentApplication.applicationType === "CORPORATION" &&
            individualsWithError.length > 0 && (
              <View className="z-40 items-center max-w-80">
                {renderIndividualsFields(individualsWithError)}
              </View>
            )}
          <View>
            <Button
              label={t("common:button.updateapplication")}
              onClick={handleSubmit(onSubmit)}
              blocked={!allFieldsFinished || !allIndividualFieldsFinished}
            />
          </View>
        </View>
        <View className="flex-grow justify-end">
          <Copyright />
        </View>
      </View>
      {isSubmitting && <Loader />}
    </ScrollView>
  );
}
